import React, { forwardRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Button, Drawer, Tooltip, Typography } from '@material-ui/core';
import { NavLink as RouterLink } from 'react-router-dom';

import StorefrontIcon from '@material-ui/icons/Storefront';
import PeopleIcon from '@material-ui/icons/People';
import SettingsIcon from '@material-ui/icons/Settings';
import HomeIcon from '@material-ui/icons/Home';
import LocalGroceryStoreIcon from '@material-ui/icons/LocalGroceryStore';
import ReceiptIcon from '@material-ui/icons/Receipt';
import EuroSymbolIcon from '@material-ui/icons/EuroSymbol';
import EventIcon from '@material-ui/icons/Event';
import WarningIcon from '@material-ui/icons/Warning';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import DeckIcon from '@material-ui/icons/Deck';
import AssessmentIcon from '@material-ui/icons/Assessment';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import HistoryIcon from '@material-ui/icons/History';

import { SidebarNav } from './components';
import { showNewDashboard } from 'common/env';
import JournalMarche from 'JournalMarche';
import UserProfile from 'UserProfile';

const SIDEBAR_WIDTH = 228;

const CustomRouterLink = forwardRef((props, ref) => (
    <div ref={ref} style={{ flexGrow: 1 }}>
        <RouterLink {...props} />
    </div>
));

const useStyles = makeStyles(theme => ({
    drawer: {
        width: SIDEBAR_WIDTH,
        flexShrink: 0
    },
    drawerPaper: {
        width: SIDEBAR_WIDTH,
        backgroundColor: '#004660',
        border: 'none',
        overflowX: 'hidden'
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },

    // ── Logo bar ──
    logoWrapper: {
        height: 52,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 14px',
        flexShrink: 0,
        margin: 'auto'
    },
    logoBar: {
        height: 52,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 14px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0
    },
    logoMark: {
        width: 28,
        height: 28,
        borderRadius: 7,
        backgroundColor: '#007AB7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        color: '#fff',
        flexShrink: 0,
        letterSpacing: '-0.5px'
    },
    logoText: {
        fontWeight: 500,
        color: '#EDEEF5',
        letterSpacing: '-0.1px',
        '& em': {
            color: '#00B4D8',
            fontStyle: 'normal'
        }
    },

    // ── User chip ──
    userChip: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '10px 14px',
        flexShrink: 0
    },
    avatar: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 600,
        color: '#fff',
        flexShrink: 0
    },
    userInfo: {
        flex: 1,
        minWidth: 0
    },
    userName: {
        fontWeight: 500,
        color: '#EDEEF5',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    roleBadge: {
        display: 'inline-block',
        fontWeight: 500,
        padding: '1px 6px',
        borderRadius: 3,
        marginTop: 2,
        backgroundColor: 'rgba(0,122,183,0.18)',
        color: '#60B8E8',
        letterSpacing: '0.3px',
        border: '1px solid rgba(0,122,183,0.2)'
    },

    // ── Footer strip ──
    footer: {
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '8px 10px',
        display: 'flex',
        gap: 4,
        flexShrink: 0
    },
    footerLink: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 8px',
        color: 'white',
        borderRadius: 6,
        cursor: 'pointer',
        textDecoration: 'none',
        transition: 'background 0.12s, color 0.12s',
        '&:hover': {
            backgroundColor: '#4872867a'
        },
        '& svg': {
            flexShrink: 0
        }
    },
    logoutBtn: {
        marginRight: 10,
        width: 30,
        height: 30,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background 0.12s, color 0.12s',
        '&:hover': {
            backgroundColor: '#343252',
            color: '#E85D24'
        }
    },

    // ── Bottom logo ──
    bottomLogo: {
        padding: '4px 14px 8px',
        textAlign: 'right',
        '& img': {
            width: 28,
            opacity: 0.5
        }
    },

    logo: {
        height: 20,
        width: 'auto',
        objectFit: 'contain'
    },
    active: {
        color: '#EDEEF5',
        borderLeftColor: '#007AB7',
        backgroundColor: '#487286',
        '& .MuiButton-startIcon': {
            color: 'white'
        }
    }
}));

// Role metadata
const ROLE_META = {
    1: {
        label: 'Administrateur',
        abbr: 'AD',
        gradient: 'linear-gradient(135deg, #D85A30, #993C1D)'
    },
    2: { label: 'Placier', abbr: 'PL', gradient: 'linear-gradient(135deg, #3A7BD5, #7F5EA3)' },
    3: { label: 'Régisseur', abbr: 'RG', gradient: 'linear-gradient(135deg, #0F6E56, #1D9E75)' }
};

const Sidebar = props => {
    const { open, variant, onClose, className, window, roleId, journalId, ...rest } = props;
    const classes = useStyles();
    const container = window !== undefined ? () => window().document.body : undefined;

    const roleMeta = ROLE_META[roleId] || ROLE_META[1];
    const userName = UserProfile.getName?.() || UserProfile.getProfile?.() || '';

    // ── Pages per module per role (moduleId → roleIndex → items) ──
    const pages = [
        [
            // Module marché — Admin
            [
                { title: 'Les marchés', href: '/markets/all', icon: <StorefrontIcon /> },
                { title: 'Les commerçants', href: '/merchants/all', icon: <PeopleIcon /> },
                { title: 'Les paramètres', href: '/settings', icon: <SettingsIcon /> }
            ],
            // Module marché — Placier
            [
                {
                    title: 'Mon marché',
                    href: showNewDashboard()
                        ? `/my-market/${journalId}/dashboard`
                        : '/dashboard/' + journalId,
                    icon: <StorefrontIcon />,
                    disable: JournalMarche.getId() === 0 || JournalMarche.getId() === '0'
                },
                { title: 'Les commerçants', href: '/merchants/all', icon: <PeopleIcon /> }
            ],
            // Module marché — Régisseur
            [
                {
                    title: 'Facturation abonné',
                    href: '/bills/subscribers/view',
                    icon: <EuroSymbolIcon />
                },
                {
                    title: 'Facturation non abonné',
                    href: '/bills/non-subscribers/view',
                    icon: <EuroSymbolIcon />
                },
                { title: 'Retard de paiement', href: '/bills/late/view', icon: <WarningIcon /> },
                {
                    title: 'Facturation annulée',
                    href: '/bills/canceled/view',
                    icon: <HighlightOffIcon />
                },
                { title: 'Historique des marchés', href: '/markets/search', icon: <HistoryIcon /> },
                { title: 'Les commerçants', href: '/merchants/all', icon: <PeopleIcon /> }
            ]
        ],
        [
            // Module domaine — Admin
            [
                { title: 'Les événements', href: '/events/all', icon: <EventIcon /> },
                { title: 'Les terrasses', href: '/new-terrace', icon: <DeckIcon /> },
                {
                    title: 'Les commerces ambulants',
                    href: '/itinerant-shop/all',
                    icon: <LocalShippingIcon />
                },
                {
                    title: 'Les autres ventes',
                    href: '/other-sales/all',
                    icon: <LocalGroceryStoreIcon />
                },
                { title: 'Les commerçants', href: '/domain-merchants/all', icon: <PeopleIcon /> }
            ],
            // Module domaine — Placier
            [
                { title: 'Facturation événement', href: '/domain-bills', icon: <EuroSymbolIcon /> },
                {
                    title: 'Facturation terrasse',
                    href: '/new-terrace-bill',
                    icon: <EuroSymbolIcon />
                },
                {
                    title: 'Facturation ambulant',
                    href: '/itinerant-shop-bills',
                    icon: <EuroSymbolIcon />
                },
                { title: 'Facturation fourrière', href: '/pound-bill', icon: <EuroSymbolIcon /> },
                {
                    title: 'Facturation autre vente',
                    href: '/other-sales-bills',
                    icon: <EuroSymbolIcon />
                },
                { title: 'Les commerçants', href: '/domain-merchants/all', icon: <PeopleIcon /> }
            ],
            // Module domaine — Régisseur (same as Placier)
            [
                { title: 'Facturation événement', href: '/domain-bills', icon: <EuroSymbolIcon /> },
                {
                    title: 'Facturation terrasse',
                    href: '/new-terrace-bill',
                    icon: <EuroSymbolIcon />
                },
                {
                    title: 'Facturation ambulant',
                    href: '/itinerant-shop-bills',
                    icon: <EuroSymbolIcon />
                },
                { title: 'Facturation fourrière', href: '/pound-bill', icon: <EuroSymbolIcon /> },
                {
                    title: 'Facturation autre vente',
                    href: '/other-sales-bills',
                    icon: <EuroSymbolIcon />
                },
                { title: 'Les commerçants', href: '/domain-merchants/all', icon: <PeopleIcon /> }
            ]
        ]
    ];

    const defaultPages = [
        [{ title: 'Tableaux de bord', href: '/reporting', icon: <AssessmentIcon /> }],
        [],
        [{ title: 'État des recettes', href: '/summary-statement', icon: <ReceiptIcon /> }]
    ];

    const disconnected = {
        title: 'Se reconnecter',
        href: '/sign-in',
        icon: <PowerSettingsNewIcon />
    };

    return (
        <Drawer
            anchor="left"
            container={container}
            ModalProps={{ keepMounted: true }}
            classes={{ paper: classes.drawerPaper }}
            className={classes.drawer}
            onClose={onClose}
            open={open}
            variant={variant}>
            <div className={clsx(classes.root, className)} {...rest}>
                <div className={classes.logoWrapper}>
                    <img
                        src="/images/logos/logo-digitmarche.png"
                        alt="Digit'Marché"
                        className={classes.logo}
                        fetchpriority="low"
                        width="auto"
                        height="25"
                    />
                </div>

                {roleId > 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(255,255,255,0.06)'
                        }}>
                        {/* Footer strip */}
                        <div className={classes.footer}>
                            <Button
                                style={{ paddingLeft: 15 }}
                                className={classes.footerLink}
                                activeClassName={classes.active}
                                onClick={onClose}
                                component={CustomRouterLink}
                                startIcon={<HomeIcon />}
                                to="/home">
                                Accueil
                            </Button>
                        </div>
                        <div className={classes.footer}>
                            <Button
                                style={{ paddingLeft: 15 }}
                                className={classes.footerLink}
                                activeClassName={classes.active}
                                onClick={onClose}
                                component={CustomRouterLink}
                                startIcon={<MailOutlineIcon />}
                                to="/mailbox">
                                mail
                            </Button>
                        </div>
                    </div>
                ) : null}

                {/* Nav */}
                <SidebarNav
                    onClose={onClose}
                    pages={pages}
                    defaultPages={defaultPages}
                    disconnected={disconnected}
                    roleId={roleId}
                />

                <div
                    style={{
                        display: 'flex',
                        gap: 7,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                        background: '#242338'
                    }}>
                    {/* Footer strip */}
                    <div className={classes.userChip}>
                        {roleId > 0 ? (
                            <>
                                <div
                                    className={classes.avatar}
                                    style={{ background: roleMeta.gradient }}>
                                    {roleMeta.abbr}
                                </div>
                                <div className={classes.userInfo}>
                                    <Typography variant="body2" className={classes.userName}>
                                        {userName}
                                    </Typography>
                                    <Typography variant="caption" className={classes.roleBadge}>
                                        {roleMeta.label}
                                    </Typography>
                                </div>
                            </>
                        ) : (
                            <div className={classes.userInfo}>
                                <div className={classes.userName}>Non connecté</div>
                            </div>
                        )}
                    </div>

                    <Tooltip title="Se déconnecter" placement="bottom">
                        <a href="/logout" className={classes.logoutBtn}>
                            <PowerSettingsNewIcon style={{ fontSize: 20 }} />
                        </a>
                    </Tooltip>
                </div>
            </div>
        </Drawer>
    );
};

Sidebar.propTypes = {
    className: PropTypes.string,
    onClose: PropTypes.func,
    open: PropTypes.bool.isRequired,
    variant: PropTypes.string.isRequired,
    roleId: PropTypes.number.isRequired,
    journalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default Sidebar;
